import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { JSX, ReactNode } from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { OPEN_REACT_QUERY_DEVTOOLS_EVENT } from "@/shared/constants/devtools";
import { shouldRetryFailedMutation } from "@/shared/utils/mutationRetry";
import { shouldEnableReactQueryDevtools } from "@/shared/utils/reactQueryDevtools";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: shouldRetryFailedMutation,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then((module) => ({
    default: module.ReactQueryDevtoolsPanel,
  }))
);

const dialogContentClassName = cn(
  "left-0 top-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col",
  "overflow-hidden rounded-none border-0 bg-[#191c24] p-0 text-[#d0d5dd]",
  "[&>button]:right-3 [&>button]:top-3 [&>button]:h-9 [&>button]:w-9 [&>button]:cursor-pointer",
  "[&>button]:rounded-md [&>button]:border-0 [&>button]:bg-transparent [&>button]:p-1.5",
  "[&>button]:text-[#d0d5dd] [&>button]:opacity-100 [&>button]:shadow-none",
  "sm:[&>button]:right-5 sm:[&>button]:top-5 sm:[&>button]:h-11 sm:[&>button]:w-11",
  "sm:[&>button]:p-2 [&>button[data-state=open]]:bg-transparent [&>button:hover]:bg-transparent",
  "[&>button:hover]:text-white [&>button]:focus:border-0 [&>button]:focus:outline-none",
  "[&>button]:focus:ring-0 [&>button]:focus:ring-offset-0 [&>button]:focus:shadow-none",
  "[&>button]:focus-visible:border-0 [&>button]:focus-visible:outline-none",
  "[&>button]:focus-visible:ring-0 [&>button]:focus-visible:ring-offset-0",
  "[&>button]:focus-visible:shadow-none [&>button_svg]:h-4 [&>button_svg]:w-4",
  "sm:[&>button_svg]:h-5 sm:[&>button_svg]:w-5"
);

export function QueryProvider({ children }: QueryProviderProps): JSX.Element {
  const [isDevtoolsOpen, setIsDevtoolsOpen] = useState(false);
  const shouldShowReactQueryDevtools = shouldEnableReactQueryDevtools(
    import.meta.env.VITE_ENVIRONMENT,
    import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS
  );

  useEffect(() => {
    if (!shouldShowReactQueryDevtools) {
      return;
    }

    function handleOpenDevtools() {
      setIsDevtoolsOpen(true);
    }

    (window as Window & { toggleDevtools?: () => void }).toggleDevtools = () =>
      setIsDevtoolsOpen((previous) => !previous);
    window.addEventListener(OPEN_REACT_QUERY_DEVTOOLS_EVENT, handleOpenDevtools);

    return () => {
      delete (window as Window & { toggleDevtools?: () => void }).toggleDevtools;
      window.removeEventListener(OPEN_REACT_QUERY_DEVTOOLS_EVENT, handleOpenDevtools);
    };
  }, [shouldShowReactQueryDevtools]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {shouldShowReactQueryDevtools ? (
        <Dialog open={isDevtoolsOpen} onOpenChange={setIsDevtoolsOpen}>
          <DialogContent className={dialogContentClassName}>
            <DialogHeader className="border-b border-[#394056] bg-[#191c24] px-4 pt-14 pb-4 sm:px-6 sm:py-4">
              <DialogTitle className="text-[#d0d5dd] text-base sm:text-lg">
                Query Inspector
              </DialogTitle>
              <DialogDescription className="text-[#98a2b3]">
                Inspect query cache, refetch states, and mutation activity.
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-hidden">
              <Suspense fallback={null}>
                <ReactQueryDevtoolsProduction
                  theme="dark"
                  style={{ height: "100%", width: "100%" }}
                />
              </Suspense>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </QueryClientProvider>
  );
}

export { queryClient };
